import { useState } from "react";
import { supabase } from "@/services/supabase";
import { useCompanyUserStore } from "@/store/companyUserStore";

interface UseSetPasswordOptions {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

interface SetPasswordData {
    password: string;
    token?: string;
}

export function useSetPassword(options?: UseSetPasswordOptions) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const setPassword = async ({ password, token }: SetPasswordData) => {
        try {
            setLoading(true);
            setError(null);

            if (!token) {
                console.log(
                    "No token provided, assuming user is already verified",
                );
            }

            const { error: updateError } = await supabase.auth.updateUser({
                password,
            });

            if (updateError) {
                throw updateError;
            }

            // Clear invite/recovery flag so protected routes allow entry
            try {
                await useCompanyUserStore.getState().clearPasswordResetRequired();
            } catch (clearError) {
                console.error(
                    "Password set but failed to clear reset flag:",
                    clearError,
                );
            }

            if (options?.onSuccess) {
                options.onSuccess();
            }

            return { success: true };
        } catch (err) {
            const nextError = err instanceof Error
                ? err
                : new Error("An error occurred while setting your password");
            setError(nextError);

            if (options?.onError) {
                options.onError(nextError);
            }

            return { success: false, error: nextError };
        } finally {
            setLoading(false);
        }
    };

    return {
        setPassword,
        loading,
        error,
    };
}
